<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import FolderTreeItem from './FolderTreeItem.vue'
import type { FolderItem } from '@/modules/documents-new/types/folder.types'
import { addPathToExpandedIds } from '@/modules/documents-new/utils/folderTree'

interface Props {
  folders: FolderItem[]
  rootFolder?: FolderItem
  selectedFolderId?: string | null
  selectable?: boolean
  expandPathTo?: string | null
  libraryRootId?: string | null
  expandLocal?: boolean
  expandGlobal?: boolean
  selectOnArrow?: boolean
}

interface Emits {
  (e: 'select', folderId: string | null, folderName: string): void
  (e: 'toggle-root'): void
  (e: 'toggle', folderId: string): void
  (e: 'expand-root'): void
}

const props = withDefaults(defineProps<Props>(), {
  selectable: true,
  expandPathTo: null,
  libraryRootId: null,
  expandLocal: false,
  expandGlobal: false,
  selectOnArrow: true,
})

const emit = defineEmits<Emits>()

const extraExpanded = ref<Set<string>>(new Set())
const extraCollapsed = ref<Set<string>>(new Set())

const folderKey = (value: string | null | undefined): string | null => {
  if (value == null || value === '') {
    return null
  }

  return String(value)
}

const isLocalName = (name: string) => {
  const normalized = name.trim().toLowerCase()

  return normalized.includes('локал') || normalized.includes('local')
}

const isGlobalName = (name: string) => {
  const normalized = name.trim().toLowerCase()

  return normalized.includes('глобал') || normalized.includes('global')
}

const isFreeExpand = computed(() => !props.expandLocal && !props.expandGlobal)

const requiredOpenIds = computed(() => {
  const ids = new Set<string>()

  for (const item of props.folders) {
    if (props.expandLocal && isLocalName(item.name)) {
      ids.add(String(item.id))
    }
    if (props.expandGlobal && isGlobalName(item.name)) {
      ids.add(String(item.id))
    }
    if (isFreeExpand.value && (isLocalName(item.name) || isGlobalName(item.name))) {
      ids.add(String(item.id))
    }
  }

  const targetId = props.expandPathTo ?? props.selectedFolderId
  if (targetId != null && targetId !== '' && props.folders.length) {
    addPathToExpandedIds(props.folders, targetId, ids, props.libraryRootId)
  }

  return ids
})

watch(
  () => [props.expandPathTo, props.selectedFolderId, props.expandLocal, props.expandGlobal] as const,
  () => {
    extraCollapsed.value = new Set()
  },
)

const foldersWithExpanded = computed(() => {
  const requiredIds = requiredOpenIds.value
  const freeExpand = isFreeExpand.value

  const updateExpanded = (
    items: FolderItem[],
    branch: 'root' | 'local' | 'global',
  ): FolderItem[] => {
    return items.map(item => {
      const isLocalRoot = branch === 'root' && isLocalName(item.name)
      const isGlobalRoot = branch === 'root' && isGlobalName(item.name)
      const nextBranch = isLocalRoot ? 'local' : isGlobalRoot ? 'global' : branch
      const inActiveLibrary = freeExpand
        || (props.expandGlobal ? nextBranch === 'global' : nextBranch === 'local')

      const key = folderKey(item.id)
      const collapsed = key != null && extraCollapsed.value.has(key)
      const manuallyOpened = key != null && extraExpanded.value.has(key) && !collapsed
      const forceRoot
        = (isLocalRoot && (freeExpand || props.expandLocal))
          || (isGlobalRoot && (freeExpand || props.expandGlobal))
      const required = inActiveLibrary && key != null && requiredIds.has(key)

      const expanded = !freeExpand && (isLocalRoot || isGlobalRoot) && !inActiveLibrary
        ? manuallyOpened
        : (!collapsed && (forceRoot || required)) || manuallyOpened

      return {
        ...item,
        expanded,
        children: item.children?.length ? updateExpanded(item.children, nextBranch) : [],
      }
    })
  }

  return updateExpanded(props.folders, 'root')
})

const handleToggle = (folderId: string) => {
  const key = folderKey(folderId)
  if (key == null) {
    return
  }

  const nextExpanded = new Set(extraExpanded.value)
  const nextCollapsed = new Set(extraCollapsed.value)
  const isRequired = requiredOpenIds.value.has(key)
  const currentlyOpen = nextCollapsed.has(key)
    ? false
    : isRequired || nextExpanded.has(key)

  if (currentlyOpen) {
    nextExpanded.delete(key)
    nextCollapsed.add(key)
  } else {
    nextCollapsed.delete(key)
    nextExpanded.add(key)
  }

  extraExpanded.value = nextExpanded
  extraCollapsed.value = nextCollapsed
  emit('toggle', folderId)
}

const handleSelect = (folderId: string | null, folderName: string) => {
  emit('select', folderId, folderName)
}

const handleRootToggle = () => {
  emit('toggle-root')
}

const rootFolderWithExpanded = computed(() => {
  if (!props.rootFolder) {
    return null
  }

  return {
    ...props.rootFolder,
    expanded: props.rootFolder.expanded,
    children: foldersWithExpanded.value,
  }
})
</script>

<template>
  <div class="folders-tree">
    <div class="folders-tree-content">
      <FolderTreeItem
        v-if="rootFolderWithExpanded"
        :key="rootFolderWithExpanded.id"
        :folder="rootFolderWithExpanded"
        :selected-folder-id="selectedFolderId"
        :selectable="selectable"
        :select-on-arrow="selectOnArrow"
        @select="handleSelect"
        @toggle="(id) => id === rootFolderWithExpanded.id ? handleRootToggle() : handleToggle(id)"
      />
      <template v-else>
        <FolderTreeItem
          v-for="f in foldersWithExpanded"
          :key="f.id"
          :folder="f"
          :selected-folder-id="selectedFolderId"
          :selectable="selectable"
          :select-on-arrow="selectOnArrow"
          @select="handleSelect"
          @toggle="handleToggle"
        />
      </template>
    </div>
  </div>
</template>

<style scoped lang="scss">
.folders-tree-content {
  padding: 8px 0;
}
</style>
